import { Send } from 'lucide-react';

import type { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';

import { useFollowUpTransmission } from '../request/cards/followups/hooks/useFollowUpTransmission';
import { FollowUpDots } from './FollowUpDots';

interface FollowUpBadgeProps {
  matterId: string;
  providerId: string;
  request: TreatmentRecordRequestSchema;
}

/**
 * Compact badge showing follow-up transmission progress.
 * Displayed in the collapsed RequestRow between date and status dropdown.
 *
 * States:
 * - Loading: Returns null (don't show skeleton in collapsed row)
 * - Auto-enabled but 0 sent: Shows "Auto" badge in blue
 * - Transmissions sent: Shows dots (e.g., ●●○)
 * - Auto-disabled and 0 sent: Returns null
 */
export const FollowUpBadge = ({
  matterId,
  providerId,
  request,
}: FollowUpBadgeProps) => {
  const { history, totalSent, maxAttempts, isAutoEnabled, isLoading } =
    useFollowUpTransmission({
      matterId,
      providerId,
      request,
    });

  // Don't show if loading
  if (isLoading) {
    return null;
  }

  // Show "Auto" state when auto-enabled but no transmissions yet
  if (totalSent === 0) {
    if (!isAutoEnabled) {
      return null;
    }

    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
        <Send className="h-2.5 w-2.5" />
        <span className="font-medium">Auto</span>
      </span>
    );
  }

  // Show progress dots when transmissions have been sent
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
      <Send className="h-2.5 w-2.5" />
      <FollowUpDots history={history} total={maxAttempts} size="xs" />
    </span>
  );
};
